export function prefersReducedMotion() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

const WEBGL_OPTS: WebGLContextAttributes = {
  alpha: true,
  antialias: false,
  depth: true,
  stencil: false,
  premultipliedAlpha: true,
  preserveDrawingBuffer: false,
  powerPreference: "default",
  failIfMajorPerformanceCaveat: false,
};

export function hasWebGL() {
  if (typeof document === "undefined") return false;
  try {
    const canvas = document.createElement("canvas");
    const gl =
      canvas.getContext("webgl", WEBGL_OPTS) ||
      canvas.getContext("experimental-webgl", WEBGL_OPTS) ||
      canvas.getContext("webgl2", WEBGL_OPTS);
    return Boolean(gl);
  } catch {
    return false;
  }
}

export function isAppleTouchDevice() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
}

function weakDevice() {
  if (typeof navigator === "undefined") return true;
  const cores = navigator.hardwareConcurrency || 2;
  const mem = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  if (cores <= 4) return true;
  if (mem != null && mem <= 4) return true;
  return false;
}

export function hallGpuQuality(): "high" | "low" {
  if (typeof window === "undefined") return "low";
  if (isAppleTouchDevice()) return "low";
  if (window.innerWidth < 700) return "low";
  if (prefersReducedMotion()) return "low";
  if (weakDevice()) return "low";
  return "high";
}

type FullscreenDoc = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};

type FullscreenEl = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};

export function isFullscreen() {
  const doc = document as FullscreenDoc;
  return Boolean(document.fullscreenElement || doc.webkitFullscreenElement);
}

export async function toggleFullscreen(el: HTMLElement) {
  const doc = document as FullscreenDoc;
  const node = el as FullscreenEl;
  try {
    if (isFullscreen()) {
      if (document.exitFullscreen) await document.exitFullscreen();
      else doc.webkitExitFullscreen?.();
      return;
    }
    if (node.requestFullscreen) await node.requestFullscreen();
    else node.webkitRequestFullscreen?.();
  } catch {
    /* browser blocked fullscreen */
  }
}

export function onFullscreenChange(handler: () => void) {
  const listener: EventListener = () => handler();
  document.addEventListener("fullscreenchange", listener);
  document.addEventListener("webkitfullscreenchange", listener);
  return () => {
    document.removeEventListener("fullscreenchange", listener);
    document.removeEventListener("webkitfullscreenchange", listener);
  };
}

export function isTypingTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target.isContentEditable;
}
